import * as React from 'react'
import * as plume from '@ulule/owl-kit-components/next'

import {
  CONTACT_PROJECT_OWNER_URL,
  PRIVACY_EMAIL,
  PRIVACY_POLICY_URL,
  SUBJECT_OPTIONS,
  SUBJECT_PLACEHOLDER,
  ZENDESK_NEW_REQUEST_URL,
} from '../content'
import { submitZendeskRequest } from '../zendesk'
import styles from './ContactForm.module.css'

// Field names — also the FormData keys read on submit.
const SUBJECT = 'subject'
const EMAIL = 'email'
const OBJECT = 'object'
const DESCRIPTION = 'description'
const ATTACHMENT = 'attachment'
const HONEYPOT = 'website'

type Status = 'idle' | 'submitting' | 'success' | 'error'

function requiredLabel(text: string): React.ReactNode {
  return (
    <>
      {text} <span className={styles.req}>*</span>
    </>
  )
}

/**
 * Contact form. There is no first-party endpoint (the page defers to Zendesk). The subject dropdown
 * selects the Zendesk ticket form, and submission is hybrid:
 *   - "simple" forms (no required custom fields) are created directly via Zendesk's anonymous end-user
 *     API — the message + email land in the ticket, with the optional attachment uploaded first;
 *   - forms with required custom/conditional fields (`handoff`) open Zendesk's hosted form via
 *     `ticket_form_id` so the user completes those fields there (tf_* prefill passed best-effort).
 * FormData is read synchronously before any await (the event target is pooled).
 */
export function ContactForm(): React.ReactElement {
  const [fileName, setFileName] = React.useState<string | null>(null)
  const [status, setStatus] = React.useState<Status>('idle')

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    const data = new FormData(event.currentTarget)
    const ticketFormId = String(data.get(SUBJECT) ?? '')
    const email = String(data.get(EMAIL) ?? '')
    const object = String(data.get(OBJECT) ?? '')
    const description = String(data.get(DESCRIPTION) ?? '')
    const attachment = data.get(ATTACHMENT)
    const file = attachment instanceof File ? attachment : null

    // Honeypot: a hidden field real users never fill. If it carries a value, treat the submit as a bot
    // and drop it silently (show success so the bot gets no signal).
    if (String(data.get(HONEYPOT) ?? '') !== '') {
      setStatus('success')
      return
    }

    const option = SUBJECT_OPTIONS.find((candidate) => candidate.ticketFormId === ticketFormId)

    // Hand off the forms whose required custom/conditional fields we don't collect.
    if (option?.handoff === true) {
      const params = new URLSearchParams({ ticket_form_id: ticketFormId })
      if (object !== '') {
        params.set('tf_subject', object)
      }
      if (description !== '') {
        params.set('tf_description', description)
      }
      if (email !== '') {
        params.set('tf_anonymous_requester_email', email)
      }
      window.location.assign(`${ZENDESK_NEW_REQUEST_URL}?${params.toString()}`)
      return
    }

    setStatus('submitting')
    try {
      await submitZendeskRequest({ ticketFormId, subject: object, description, requesterEmail: email, file })
      setStatus('success')
    } catch {
      setStatus('error')
    }
  }

  if (status === 'success') {
    return (
      <div className={styles.success} role="status">
        <span className={styles.success__icon}>
          <plume.glyphs.stroke.Check size={22} />
        </span>
        <h2 className={styles.success__title}>Votre demande a bien été envoyée&nbsp;!</h2>
        <p className={styles.success__text}>
          Notre équipe d'assistance vous répondra par e-mail dans les meilleurs délais. Pensez à vérifier
          vos courriers indésirables.
        </p>
      </div>
    )
  }

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      {/* Honeypot: hidden from real users (off-screen + aria-hidden); a filled value marks a bot. */}
      <div className={styles.hp} aria-hidden="true">
        <label>
          Ne remplissez pas ce champ
          <input type="text" name={HONEYPOT} tabIndex={-1} autoComplete="off" />
        </label>
      </div>
      <div className={styles.warn}>
        <strong>Attention&nbsp;:</strong> vous vous apprêtez à contacter l'équipe Ulule. Pour toute question
        sur un projet hébergé, merci de toujours commencer par{' '}
        <a href={CONTACT_PROJECT_OWNER_URL}>contacter directement le porteur de projet</a>.
      </div>

      <plume.Field label={requiredLabel('Quel est le sujet de votre demande ?')} htmlFor="contact-subject">
        <select id="contact-subject" name={SUBJECT} defaultValue="" required>
          <option value="" disabled>
            {SUBJECT_PLACEHOLDER}
          </option>
          {SUBJECT_OPTIONS.map((option) => (
            <option key={option.ticketFormId} value={option.ticketFormId}>
              {option.label}
            </option>
          ))}
        </select>
      </plume.Field>

      <plume.TextField
        label={requiredLabel('Adresse e-mail')}
        name={EMAIL}
        type="email"
        placeholder="votre@email.com"
        required
      />

      <plume.TextField
        label={requiredLabel('Objet')}
        name={OBJECT}
        type="text"
        placeholder="Résumez votre demande en quelques mots"
        required
      />

      <plume.TextAreaField
        label={requiredLabel('Description')}
        name={DESCRIPTION}
        placeholder="Saisissez les détails de votre demande. Un membre de notre équipe d'assistance répondra dans les plus brefs délais."
        rows={5}
        required
      />

      <plume.Field label="Pièce jointe" context="facultatif — image ou capture d'écran">
        <label className={styles.file}>
          <plume.glyphs.stroke.Download size={16} />
          {fileName ?? 'Ajouter un fichier ou glisser-déposer ici'}
          <input
            className={styles.file__input}
            name={ATTACHMENT}
            type="file"
            onChange={(event) => setFileName(event.target.files?.[0]?.name ?? null)}
          />
        </label>
      </plume.Field>

      {status === 'error' ? (
        <p className={styles.error} role="alert">
          L'envoi a échoué. Merci de réessayer, ou d'écrire directement via{' '}
          <a href={ZENDESK_NEW_REQUEST_URL}>notre centre d'aide</a>.
        </p>
      ) : null}

      <plume.RoundButton type="submit" color="yellow" size="big" disabled={status === 'submitting'}>
        {status === 'submitting' ? 'Envoi en cours…' : 'Envoyer ma demande'}
      </plume.RoundButton>

      <p className={styles.privacy}>
        Ulule collecte vos données pour permettre de répondre à vos questions et d'assurer la relation
        clients. Pour exercer vos droits, écrivez à <a href={`mailto:${PRIVACY_EMAIL}`}>{PRIVACY_EMAIL}</a>.
        Pour plus de détails&nbsp;: <a href={PRIVACY_POLICY_URL}>Politique de Confidentialité d'Ulule</a>.
      </p>
    </form>
  )
}
